import { Button, IconButton, TextareaAutosize, TextField } from "@mui/material";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Box from "elements/Box";
import { ProjectConfig } from "Global";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import EventIcon from "@mui/icons-material/Event";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import App from "elements/HtmlEditor/App";
import AutocompleteSantoral from "./utils/AutocompleteSantoral";
import { useSnackbar, withSnackbar } from "notistack";
import moment from "moment";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import LangTabs from "elements/LangTabs";
import Cookies from "universal-cookie";

const MIN_DATE = moment().subtract(350, "years");

export default withSnackbar(function AniversariosEditor(props: any) {
  /*SNACKBAR FUNCTION FOR NOTIFICATIONS*/
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  /*============================================= */

  /*=======================================
    GET PROJECT ID AND ESTIMATE ID FROM URL
  =======================================*/
  let { aniversario_id } = useParams();
  /*=======================================
  =======================================*/

  const navigate = useNavigate();
  const defaultValues: any = {
    aniversario_id: aniversario_id ? aniversario_id : "null",
    image: "",
    name: "",
    birth_date: null,
    death_date: null,
    ordination_date: null,
    profession_date: null,
    santoral_name: "",
    santoral_id: null,
    bioHTML: "",
    bioJSON: null,
  };

  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState(defaultValues);
  const [files, setFiles] = useState<any>([]);

  // i18n: idioma actual y traducciones
  const [currentLangId, setCurrentLangId] = useState<string>("1");
  const [translations, setTranslations] = useState<Record<string, any>>({});
  const originalNamesRef = useRef<Record<string, string>>({});
  const initialBiographyHtmlRef = useRef<Record<string, string>>({});
  const [savingNames, setSavingNames] = useState(false);
  const [mergingJuan, setMergingJuan] = useState(false);
  const [juanAlreadyMerged, setJuanAlreadyMerged] = useState(false);

  interface pdfData {
    file_id: any;
    file: any;
    name: string;
    estado: string;
  }

  const [filesPdf, setFilesPdf] = useState<pdfData[]>([]);

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  /*=======================================
    TEXT EDITOR
  =======================================*/
  const [initialContent, setInitialContent] = useState<any>(null);
  // State para mantener el contenido actual del editor (evita problemas de closure)
  const [currentEditorContent, setCurrentEditorContent] = useState<{html: any, json: any}>({html: '', json: null});

  // Handler para cambio de idioma
  const handleLangChange = (newLangId: string) => {
    // Primero guardamos el contenido actual del idioma que estamos dejando
    const savedContent = { ...currentEditorContent };
    
    // Actualizar el estado según el idioma actual
    if (currentLangId === "1") {
      // Estábamos en español, guardamos en formValues
      setFormValues((prev: any) => ({
        ...prev,
        bioHTML: savedContent.html,
        bioJSON: savedContent.json,
      }));
    } else {
      // Estábamos en otro idioma, guardamos en translations
      setTranslations(prev => ({
        ...prev,
        [currentLangId]: {
          ...prev[currentLangId],
          bioHTML: savedContent.html,
          bioJSON: savedContent.json,
        }
      }));
    }

    // Cambiar al nuevo idioma
    setCurrentLangId(newLangId);
    
    // Cargar el contenido del nuevo idioma
    // Usamos setTimeout para asegurar que los estados se hayan actualizado
    setTimeout(() => {
      if (newLangId === "1") {
        // Para español, necesitamos el bioJSON actualizado
        // Como acabamos de hacer setFormValues, usamos el savedContent si venimos de español
        // o el formValues actual si venimos de otro idioma
        setFormValues((prev: any) => {
          setInitialContent(prev.bioJSON);
          return prev;
        });
      } else {
        // Para otros idiomas, usar translations
        setTranslations(prev => {
          const trans = prev[newLangId];
          setInitialContent(trans?.bioJSON || null);
          return prev;
        });
      }
    }, 0);
  };

  // Helper para obtener el nombre según idioma
  const getCurrentName = () => {
    if (currentLangId === "1") return formValues.name || "";
    return translations[currentLangId]?.name || "";
  };

  // Helper para actualizar traducciones
  const updateTranslation = (field: string, value: any) => {
    setTranslations(prev => ({
      ...prev,
      [currentLangId]: {
        ...prev[currentLangId],
        [field]: value
      }
    }));
  };

  // Handler para cambio de nombre
  const handleNameChange = (e: { target: { value: any } }) => {
    const value = e.target.value;
    if (currentLangId === "1") {
      setFormValues({ ...formValues, name: value });
    } else {
      updateTranslation('name', value);
    }
  };

  const handleEditorChange = (htmlContent: any, jsonContent: any) => {
    // Guardar en el ref para usar al cambiar de idioma
    setCurrentEditorContent({html: htmlContent, json: jsonContent});
    
    if (currentLangId === "1") {
      setFormValues((prev: any) => ({
        ...prev,
        bioHTML: htmlContent,
        bioJSON: jsonContent,
      }));
    } else {
      updateTranslation('bioHTML', htmlContent);
      updateTranslation('bioJSON', jsonContent);
    }
  };
  /*=======================================
  =======================================*/

  /*=======================================
    GET DETAILS IF EDITING
  =======================================*/
  useEffect(() => {
    if (aniversario_id) {
      // Cargar datos principales
      axios
        .get(ProjectConfig.api_url, {
          params: {
            request: "aniversarios_details",
            id: aniversario_id,
          },
        })
        .then((response) => {
          console.log("formValues", response.data);
          originalNamesRef.current["1"] = String(response.data.name || "");
          initialBiographyHtmlRef.current["1"] = String(response.data.bioHTML || "");
          setFormValues(response.data);
          setInitialContent(response.data.bioJSON);
          setFilesPdf(response.data.files);
        })
        .catch((err) => {
          console.log(err.data);
        });

      // Cargar traducciones
      axios
        .get(ProjectConfig.api_url, {
          params: {
            request: "aniversarios_translations",
            id: aniversario_id,
          },
        })
        .then((response) => {
          const transData = response.data;
          const transMap: Record<string, any> = {};
          transData.forEach((t: any) => {
            originalNamesRef.current[String(t.lang_id)] = String(t.name || "");
            initialBiographyHtmlRef.current[String(t.lang_id)] = String(t.bioHTML || "");
            transMap[String(t.lang_id)] = {
              name: t.name,
              bioHTML: t.bioHTML,
              bioJSON: t.bioJSON,
            };
          });
          setTranslations(transMap);
        })
        .catch((err) => {
          console.log(err.data);
        });
    }
  }, []);
  /*=======================================
  =======================================*/

  /*=======================================
    SUBMIT
  =======================================*/
  const [progress, setProgress] = useState(0);

  // Guardar únicamente nombres: no reenviar fechas cero ni biografías antiguas.
  // El backend valida cada actualización y utiliza consultas preparadas.
  const handleSaveNames = async () => {
    if (!aniversario_id || originalNamesRef.current["1"] === undefined || savingNames) {
      return;
    }
    const entries = [
      { langId: "1", name: String(formValues.name || "").trim() },
      ...Object.entries(translations).filter(([langId]) => langId !== "1").map(([langId, trans]) => ({
        langId,
        name: String(trans?.name || "").trim(),
      })),
    ];
    const changes = entries.filter(({ langId, name }) =>
      name !== String(originalNamesRef.current[langId] ?? "").trim()
    );
    if (changes.length === 0) {
      enqueueSnackbar("No hay nombres modificados", { variant: "info" });
      return;
    }
    if (changes.some(({ langId, name }) =>
      !name || originalNamesRef.current[langId] === undefined
    )) {
      enqueueSnackbar("Hay un nombre vacío o una traducción aún no creada", { variant: "error" });
      return;
    }

    setSavingNames(true);
    try {
      for (const { langId, name } of changes) {
        const data = new FormData();
        data.append("formValues", JSON.stringify({
          name_only: true, name, aniversario_id,
        }));
        const response = langId === "1"
          ? await axios.post(ProjectConfig.api_url, data, {
              params: { request: "add_edit_aniversarios" },
            })
          : await axios.post(ProjectConfig.api_url, data, {
              params: {
                request: "save_aniversarios_translation",
                aniversario_id, lang_id: langId,
              },
            });
        const success = response.data?.success === true;
        if (!success) {
          throw new Error("No se pudo actualizar el nombre del idioma " + langId);
        }
        originalNamesRef.current[langId] = name;
      }
      enqueueSnackbar("Nombres actualizados sin modificar otros datos", { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar(err?.message || "Error al actualizar los nombres", { variant: "error" });
    } finally {
      setSavingNames(false);
    }
  };

  // Fusión específica 349 -> 119, previa certificación y confirmación visible.
  useEffect(() => {
    if (String(aniversario_id) !== "119") return;
    axios.get(ProjectConfig.api_url, {
      params: { request: "preview_juan_morrell_merge" },
      headers: { Authorization: `Bearer ${new Cookies().get("ltkn") || ""}` },
    }).then(({ data }) => {
      setJuanAlreadyMerged(data?.merged === true);
    }).catch(() => { /* No asumir que se fusionó si falla la consulta. */ });
  }, [aniversario_id]);

  const handleMergeJuan = async () => {
    if (String(aniversario_id) !== "119" || mergingJuan) return;
    setMergingJuan(true);
    try {
      const headers = {
        Authorization: `Bearer ${new Cookies().get("ltkn") || ""}`,
      };
      const preview = await axios.get(ProjectConfig.api_url, {
        params: { request: "preview_juan_morrell_merge" },
        headers,
      });
      if (!preview.data?.success || !preview.data?.ready) {
        throw new Error(preview.data?.message || "No se puede verificar la fusión.");
      }
      const details = preview.data.details || {};
      const confirmed = window.confirm(
        "¿Unificar las fichas 119 y 349 de P. Juan Morrell Xifré?\n\n" +
        "Se conservará la ficha 119, se integrarán datos faltantes, traducciones y documentos, " +
        "y la ficha 349 se retirará tras guardar una copia íntegra para auditoría.\n\n" +
        "Fechas coincidentes: " + (details.matching_dates || 0) +
        ". Traducciones en origen: " + (details.translations_duplicate || 0) +
        ". Documentos a mover: " + (details.files_to_move || 0) +
        ".\n\nNo se modificará ninguna otra ficha."
      );
      if (!confirmed) return;
      const body = new FormData();
      body.append("confirmed_name", "P. Juan Morrell Xifré");
      const apply = await axios.post(ProjectConfig.api_url, body, {
        params: { request: "merge_juan_morrell_aniversarios" },
        headers,
      });
      if (apply.data?.success !== true || apply.data?.removed_duplicate_id !== 349) {
        throw new Error(apply.data?.message || "No se completó la fusión.");
      }
      setJuanAlreadyMerged(true);
      enqueueSnackbar("Fichas unificadas y copia histórica preservada", { variant: "success" });
      window.location.reload();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || error?.message ||
        "Error al unificar las fichas", { variant: "error" });
    } finally {
      setMergingJuan(false);
    }
  };

  const handleSubmit = async () => {
    console.log(formValues);

    //FILES NEEDS TO BE UPLOADED USING FORMDATA, APART FROM THE OTHER FIELDS
    const formData = new FormData();
    formData.append("files", files);
    formData.append("formValues", JSON.stringify(formValues));
    // Agregar archivos al FormData
    for (let i = 0; i < files.length; i++) {
      formData.append(`files[${i}]`, files[i]);
    }

    // Agregar archivos PDF al FormData
    for (let i = 0; i < filesPdf.length; i++) {
      formData.append(`filesPdf[${i}][file_id]`, filesPdf[i].file_id);
      formData.append(`filesPdf[${i}][file]`, filesPdf[i].file);
      formData.append(`filesPdf[${i}][name]`, filesPdf[i].name);
      formData.append(`filesPdf[${i}][estado]`, filesPdf[i].estado);
    }

    //AXIOS
    try {
      const response = await axios.post(ProjectConfig.api_url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          setProgress(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        },
        params: {
          request: "add_edit_aniversarios",
        },
      });

      console.log(response);
      if (response.data) {
        // Guardar traducciones
        const aniv_id = formValues.aniversario_id !== "null" 
          ? formValues.aniversario_id 
          : response.data.aniversario_id || formValues.aniversario_id;

        // Guardar cada traducción
        for (const [langId, trans] of Object.entries(translations)) {
          if (trans && (trans as any).name) {
            const transFormData = new FormData();
            transFormData.append("formValues", JSON.stringify(trans));
            
            await axios.post(ProjectConfig.api_url, transFormData, {
              params: {
                request: "save_aniversarios_translation",
                aniversario_id: aniv_id,
                lang_id: langId,
              },
            });
          }
        }

        navigate("/admin/aniversarios");
      } else {
        enqueueSnackbar("Error al guardar", { variant: "error" });
      }
    } catch (err: any) {
      console.log(err.data);
      enqueueSnackbar("Error al guardar", { variant: "error" });
    }
  };
  /*====================================
  =====================================*/

  const handleAutocompleteChange = (santoral_name: any, santoral_id: any) => {
    console.log(santoral_name, santoral_id);
    setFormValues({
      ...formValues,
      santoral_name: santoral_name,
      santoral_id: santoral_id,
    });
  };

  const borrarPorIdPdf = (file_id: number) => {
    borrar_file(file_id);
    setFilesPdf((prevPdf) =>
      prevPdf.filter((file) => file.file_id !== file_id)
    );
  };

  const borrar_file = (directorio_id: Number) => {
    const formData = new FormData();
    formData.append("archivo_id", JSON.stringify(directorio_id));
    //AXIOS
    axios
      .post(ProjectConfig.api_url, formData, {
        onUploadProgress: (progressEvent) => {
          setProgress(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        },
        params: {
          request: "delete_archivo",
        },
      })
      .then((response) => {
        console.log(response);
        if (response.data) {
          setFormValues({
            ...formValues,
            filePdf: "",
            file_id: 0,
          });
        } else {
          enqueueSnackbar("Error al borrar", { variant: "error" });
        }
      })
      .catch((err) => {
        console.log(err.data);
      });
  };

  const [ident, setident] = useState(1000000001);
  const add_file = () => {
    setFilesPdf((current) => [
      ...current,
      {
        file_id: ident,
        file: "",
        name: "",
        estado: "nuevo",
      },
    ]);
    setident(ident + 1);
  };

  const handleFileInputChange = (
    file_id: number,
    file: any,
    name: string,
    estado: string
  ) => {
    actualizarFile(file_id, file, name, estado);

    //
  };

  const actualizarFile = (
    file_id: number,
    file: any,
    name: string,
    estado: string
  ) => {
    if (estado == "nuevo") {
      setFilesPdf((prevFile) =>
        prevFile.map((pdfRow) =>
          pdfRow.file_id === file_id
            ? {
                ...pdfRow,
                file: file,
                name: name,
              }
            : pdfRow
        )
      );
    } else {
      setFilesPdf((prevFile) =>
        prevFile.map((pdfRow) =>
          pdfRow.file_id === file_id
            ? {
                ...pdfRow,
                file: file,
                name: name,
                estado: "editable",
              }
            : pdfRow
        )
      );
    }

    
  };

  const handleTituloInputChange = (id: any, value: any) => {
    actualizarTituloPorIdPdf(id, value);

    //
  };

  const actualizarTituloPorIdPdf = (file_id: any, name: any) => {
    setFilesPdf((prevFile) =>
      prevFile.map((pdfRow) =>
        pdfRow.file_id === file_id
          ? {
              ...pdfRow,
              name: name,
            }
          : pdfRow
      )
    );
  };

  return (
    <div id="userForm">
      <div className="userForm_right">
        <Box
          id="aniversarios_editor"
          className="box_editor"
          title="Aniversario"
          subtitle="Editar"
        >
          {/* Language Tabs */}
          <LangTabs currentLangId={currentLangId} onChange={handleLangChange} />

          <div className="input_group">
            <div className="input_row">
              {/* UPLOAD button with preview - Solo en español */}
              {currentLangId === "1" && (
              <div className="drawerImagesContainer">
                <div
                  className="drawerImagesContainerDiv"
                  onDrop={(e) => {
                    e.preventDefault();
                    // UPDATE FILE
                    const files = e.dataTransfer.files;
                    setFiles(files[0]);
                    const fileArray = Array.from(files).map((file: any) =>
                      URL.createObjectURL(file)
                    );
                    setFormValues({
                      ...formValues,
                      image: fileArray[0],
                    });
                    e.currentTarget.classList.remove("dropzone_active");
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add("dropzone_active");
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("dropzone_active");
                  }}
                >
                  {formValues.image && <img src={formValues.image} />}

                  <label
                    id="dropFileDiv"
                    className={formValues.image && "showOnlyOnHover"}
                  >
                    <IconButton
                      aria-label="delete"
                      size="small"
                      className="addFile_button"
                      component="span"
                    >
                      <CameraAltIcon />
                      <input
                        type="file"
                        multiple
                        hidden
                        onChange={(e) => {
                          const files = e.target.files;
                          if (files) {
                            // UPDATE FILE
                            setFiles(files[0]);
                            const fileArray = Array.from(files).map(
                              (file: any) => URL.createObjectURL(file)
                            );
                            setFormValues({
                              ...formValues,
                              image: fileArray[0],
                            });
                          }
                        }}
                      />
                    </IconButton>
                  </label>
                </div>
              </div>
              )}

              <div className="inputColumn">
                <div className="input_row">
                  <TextField
                    id="name"
                    name="name"
                    label="Nombres"
                    type="text"
                    required
                    fullWidth
                    value={getCurrentName()}
                    onChange={handleNameChange}
                  />

                  {/* Santoral - Solo en español */}
                  {currentLangId === "1" && (
                  <AutocompleteSantoral
                    name={formValues.santoral_name}
                    onChange={handleAutocompleteChange}
                  />
                  )}
                </div>

                {/* Fechas - Solo en español */}
                {currentLangId === "1" && (
                <>
                <div className="input_row">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <MobileDatePicker
                      label="Fecha de nacimiento"
                      inputFormat="DD/MM/YYYY"
                      minDate={MIN_DATE}
                      value={formValues.birth_date}
                      InputProps={{
                        endAdornment: <EventIcon />,
                      }}
                      showToolbar={false}
                      closeOnSelect
                      onChange={(newValue) => {
                        setFormValues({
                          ...formValues,
                          ["birth_date"]: moment(newValue).format("YYYY-MM-DD"),
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </LocalizationProvider>

                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <MobileDatePicker
                      label="Fecha de profesión religiosa"
                      inputFormat="DD/MM/YYYY"
                      minDate={MIN_DATE}
                      value={formValues.profession_date}
                      InputProps={{
                        endAdornment: <EventIcon />,
                      }}
                      showToolbar={false}
                      closeOnSelect
                      onChange={(newValue) => {
                        setFormValues({
                          ...formValues,
                          ["profession_date"]:
                            moment(newValue).format("YYYY-MM-DD"),
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </LocalizationProvider>

                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <MobileDatePicker
                      label="Fecha ordenación sacerdotal"
                      inputFormat="DD/MM/YYYY"
                      minDate={MIN_DATE}
                      value={formValues.ordination_date}
                      InputProps={{
                        endAdornment: <EventIcon />,
                      }}
                      showToolbar={false}
                      closeOnSelect
                      onChange={(newValue) => {
                        setFormValues({
                          ...formValues,
                          ["ordination_date"]:
                            moment(newValue).format("YYYY-MM-DD"),
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </LocalizationProvider>
                </div>
                <div className="input_row">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <MobileDatePicker
                      label="Fecha de fallecimiento"
                      inputFormat="DD/MM/YYYY"
                      minDate={MIN_DATE}
                      value={formValues.death_date}
                      InputProps={{
                        endAdornment: <EventIcon />,
                      }}
                      showToolbar={false}
                      closeOnSelect
                      onChange={(newValue) => {
                        setFormValues({
                          ...formValues,
                          ["death_date"]: moment(newValue).format("YYYY-MM-DD"),
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </LocalizationProvider>
                </div>
                </>
                )}
              </div>
            </div>
          </div>

          <App
            placeholder="Biografía"
            handleEditorChange={handleEditorChange}
            content={initialContent}
            htmlContent={initialBiographyHtmlRef.current[currentLangId] || ""}
            preferHtml={!initialContent}
            emitHumanChangesOnly
          />

          {/* PDFs - Solo en español */}
          {currentLangId === "1" && (
          <>
          {filesPdf.length > 0 &&
            filesPdf.map((fileRow, index) => (
              <div key={index}>
                {/* <p>  Hola </p> */}
                {/* <p>  {fileRow.name} </p> */}
                <label>Titulo</label>
                <div className="inputColumn">
                  <input
                    type="text"
                    name={"titulo_" + index}
                    id={"titulo_" + index}
                    onChange={(e) =>
                      actualizarTituloPorIdPdf(fileRow.file_id, e.target.value)
                    }
                    value={fileRow.name}
                  />
                </div>
                {/* <br/> */}
                <br />
                <div className="drawerImagesContainer">
                  <div
                    id="multimediaFileDropBox"
                    className={
                      "drawerImagesContainerDiv " +
                      (fileRow.file != "" ? "hasFile" : "")
                    }
                    onDrop={(e) => {
                      e.preventDefault();
                      // UPDATE FILE
                      const files = e.dataTransfer.files;
                      const fileName = files[0].name;
                      console.log("fileName 1");
                      console.log(fileName);
                      handleFileInputChange(
                        fileRow.file_id,
                        files[0],
                        fileName,
                        fileRow.estado
                      );

                      const fileArray = Array.from(files).map((file: any) =>
                        URL.createObjectURL(file)
                      );

                      e.currentTarget.classList.remove("dropzone_active");
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add("dropzone_active");
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove("dropzone_active");
                    }}
                  >
                    {fileRow.file && (
                      <span className="fileUploaded">
                        <AttachFileIcon />
                        {fileRow.name}
                      </span>
                    )}

                    <label
                      id="dropFileDiv"
                      className={fileRow.file && "showOnlyOnHover"}
                    >
                      <IconButton
                        aria-label="delete"
                        size="small"
                        className="addFile_button"
                        component="span"
                      >
                        <FileUploadIcon />

                        <input
                          type="file"
                          multiple
                          hidden
                          accept=".pdf"
                          onChange={(e) => {
                            const files = e.target.files;
                            if (files) {
                              //get file name
                              const fileName = files[0].name;
                              // console.log("fileName 2")
                              // console.log(fileName)
                              // UPDATE FILE
                              handleFileInputChange(
                                fileRow.file_id,
                                files[0],
                                fileName,
                                fileRow.estado
                              );
                              const fileArray = Array.from(files).map(
                                (file: any) => URL.createObjectURL(file)
                              );
                            }
                          }}
                        />
                      </IconButton>
                      Arrastra aquí un archivo o da click para subirlo.
                    </label>
                  </div>
                  {fileRow.file != "" && (
                    <Button
                      onClick={() => borrarPorIdPdf(fileRow.file_id)}
                      variant="contained"
                      className="button_1"
                    >
                      Borrar
                    </Button>
                  )}
                </div>
              </div>
            ))}

          <>
            <br></br>
            <br></br>
            {/* <br></br> */}
            <Button onClick={add_file} variant="contained" className="button_1">
              Agregar PDF
            </Button>
          </>
          </>
          )}

          {String(aniversario_id) === "119" && !juanAlreadyMerged && (
            <div className="buttons">
              <Button
                onClick={handleMergeJuan}
                disabled={mergingJuan || savingNames}
                variant="outlined"
                className="button_2"
              >
                {mergingJuan ? "Comprobando fichas…" : "Unificar duplicado 349"}
              </Button>
            </div>
          )}
          <div className="buttons">
            <Button
              onClick={() => navigate(-1)}
              variant="outlined"
              className="button_2"
            >
              Cancelar
            </Button>
            {aniversario_id && (
              <Button
                onClick={handleSaveNames}
                disabled={savingNames}
                variant="outlined"
                className="button_2"
              >
                {savingNames ? "Guardando nombres…" : "Guardar solo nombres"}
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              disabled={savingNames}
              variant="contained"
              className="button_1"
            >
              Guardar
            </Button>
          </div>
        </Box>
      </div>
    </div>
  );
});
